/**
 * partsRuntime — regras COMPARTILHADAS de montagem dos avatares (carro/pessoa) a
 * partir das peças (buildCarParts/buildPersonParts).
 *
 * FONTE ÚNICA DE VERDADE: usadas TANTO pelo jogo (Fleet, renderização instanciada)
 * QUANTO pela prévia admin (/models, um Group por avatar). Assim a aparência é
 * idêntica nos dois e qualquer refinamento de modelo vale em produção e na prévia.
 *
 * O que é específico de cada lado e NÃO mora aqui: o Fleet faz instancing +
 * animação (rodas/pernas) e aplica a escala de mundo; o /models mostra um avatar
 * estático em pose neutra. A SELEÇÃO de peças e as CORES — o que define a aparência
 * do modelo — ficam aqui, num lugar só.
 */
import * as THREE from 'three';
import { personDepthMaterial } from './person/deform.js';
import { applyPersonPose } from './person/pose.js';

// cor temporária reaproveitada por partColor(): 'paintDark'/'paintShade'/'topDark' derivam um
// tom do jogador e eram um THREE.Color novo por peça a cada spawn (dezenas por
// jogador). O valor é lido e convertido para string na hora — nada escapa daqui.
const _derived = new THREE.Color();

/**
 * A peça aparece para esta aparência? Peças SEM `variant` são base (sempre
 * aparecem). Peças com `variant` (modelo do carro; cabelo/roupa/pés da pessoa) só
 * aparecem quando a opção da aparência combina (`option` pode ser lista).
 * `when` restringe combinações, como blusas ajustadas à cintura da saia.
 * `hideWhen` aceita uma regra ou uma lista de regras alternativas de cobertura.
 */
export function partVisible(def, appearance) {
  if (def.when && (!appearance || Object.entries(def.when).some(([key,value])=>appearance[key]!==value))) return false;
  // `hideWhen`: peça BASE que some quando uma variante a cobre por completo.
  // Usado no pé nu, que fica coberto pelo sapato. Os dois compartilham o
  // pivô da passada; emitir também a pele esconderia geometria sob o calçado.
  if (def.hideWhen && appearance) {
    const rules = Array.isArray(def.hideWhen) ? def.hideWhen : [def.hideWhen];
    if (rules.some(rule => Array.isArray(rule.option) ? rule.option.includes(appearance[rule.variant]) : appearance[rule.variant] === rule.option)) return false;
  }
  if (!def.variant) return true;
  if (!appearance) return false;
  const v = appearance[def.variant];
  return Array.isArray(def.option) ? def.option.includes(v) : v === def.option;
}

/**
 * Cor (hex) com que a peça deve ser tingida, conforme a aparência — ou `null` se a
 * peça usa material fixo (não tinge). Branco quando a chave não tem cor definida
 * (mesmo fallback do Fleet → instanceColor 1,1,1).
 */
export function partColor(def, colors) {
  if (!def.color) return null;
  // 'paintDark' = detalhe no MESMO tom da pintura do jogador, porém mais ESCURO
  // (ex.: rodas/faixas do Porsche acompanham a cor do carro em vez de vermelho fixo).
  if (def.color === 'paintDark') {
    return _derived.set((colors && colors.paint) || '#888888').multiplyScalar(0.55).getStyle();
  }
  // 'paintShade' = pintura do jogador apenas 15% mais escura — para SULCOS de chapa
  // (frestas de porta do Fusca), que no carro real são sombra da própria cor, não
  // uma peça preta aplicada.
  if (def.color === 'paintShade') {
    return _derived.set((colors && colors.paint) || '#888888').multiplyScalar(0.85).getStyle();
  }
  // 'topDark' = MESMO tom da roupa de cima, porém 10% mais escuro (colarinho/gola/punho)
  if (def.color === 'topDark') {
    return _derived.set((colors && colors.top) || '#888888').multiplyScalar(0.9).getStyle();
  }
  return (colors && colors[def.color]) || '#ffffff';
}

/**
 * Monta um Group ESTÁTICO (pose neutra, sem animação) com as peças visíveis do
 * avatar — a representação "1 avatar = 1 Group" usada pela prévia /models. Usa
 * EXATAMENTE partVisible()/partColor() (as mesmas regras do jogo). Para peças que
 * tingem, clona o material e aplica a cor (como o instanceColor faz no jogo).
 *
 * @param {Array}  parts       saída de buildCarParts()/buildPersonParts()
 * @param {Object} appearance  { model, colors } (carro) | { hair,top,…, colors } (pessoa)
 */
/**
 * Par do buildAvatarGroup: esvazia um container de grupos de avatar DESCARTANDO os
 * materiais que o tingimento clonou. `def.mat` e o material COMPARTILHADO da peca —
 * nunca descartar; so o clone. Sem isto cada troca de retrato (banner de conquista,
 * chips do rodape) deixa ~15 materiais vivos no renderer, que so cresce num telao
 * de 8 h.
 */
export function clearAvatarGroups(container) {
  for (let i = container.children.length - 1; i >= 0; i--) {
    const g = container.children[i];
    for (const mesh of g.children) {
      const def = mesh.userData.part;
      if (def && mesh.material !== def.mat) mesh.material.dispose();
      mesh.customDepthMaterial?.dispose();
    }
    container.remove(g);
  }
}

export function buildAvatarGroup(parts, appearance) {
  const colors = appearance && appearance.colors;
  const group = new THREE.Group();
  for (const def of parts) {
    if (!partVisible(def, appearance)) continue;
    let mat = def.mat;
    const col = partColor(def, colors);
    if (col !== null) {
      mat = def.mat.clone();
      mat.color.set(col);
    }
    const mesh = new THREE.Mesh(def.geo, mat);
    if(mat.personMode)mesh.customDepthMaterial=personDepthMaterial(mat);
    if (def.pos) mesh.position.set(def.pos[0], def.pos[1], def.pos[2]);
    if (def.rot) mesh.rotation.set(def.rot[0], def.rot[1], def.rot[2]);
    mesh.userData.part = def; // p/ ANIMAR retratos fora do Fleet (ex.: toast de conquista)
    group.add(mesh);
  }
  if (group.children.some(mesh=>mesh.userData.part.anim==='armL')) group.position.y=applyPersonPose(group,{});
  return group;
}
